import type { CPMOutcome, DurationKey, Task } from "./types";

/**
 * Calendar day d spans [d, d+1). Same relative Mon-start week used for the Lean
 * board's weekend shading: position 0-4 (mod 7) is Mon-Fri, 5-6 is the weekend.
 *
 * If `t` falls within a weekend span, push it forward to the start of the next Monday.
 */
export function skipWeekendForward(t: number): number {
  const dayFloor = Math.floor(t + 1e-9);
  const pos = ((dayFloor % 7) + 7) % 7;
  if (pos === 5) return dayFloor + 2;
  if (pos === 6) return dayFloor + 1;
  return t;
}

/** If `t` falls within a weekend span, pull it back to the end of the preceding Friday. */
function skipWeekendBackward(t: number): number {
  const dayFloor = Math.floor(t - 1e-9);
  const pos = ((dayFloor % 7) + 7) % 7;
  if (pos === 5) return dayFloor;
  if (pos === 6) return dayFloor - 1;
  return t;
}

/**
 * Advances `start` by `dur` days of actual work, skipping weekends entirely (they
 * consume no duration) when `allowWeekendWork` is off. With it on, this is just
 * `start + dur`, same as before weekend-awareness existed.
 */
export function advanceByWorkingDays(start: number, dur: number, allowWeekendWork: boolean): number {
  const d = Math.max(0, dur);
  if (allowWeekendWork) return start + d;
  let cur = skipWeekendForward(start);
  let remaining = d;
  while (remaining > 1e-9) {
    const dayFloor = Math.floor(cur + 1e-9);
    const capacity = dayFloor + 1 - cur;
    const consume = Math.min(remaining, capacity);
    cur += consume;
    remaining -= consume;
    if (remaining > 1e-9) cur = skipWeekendForward(cur);
  }
  return cur;
}

/** The mirror of advanceByWorkingDays for the backward pass: latest start given a latest finish. */
export function retreatByWorkingDays(finish: number, dur: number, allowWeekendWork: boolean): number {
  const d = Math.max(0, dur);
  if (allowWeekendWork) return finish - d;
  let cur = skipWeekendBackward(finish);
  let remaining = d;
  while (remaining > 1e-9) {
    const dayFloor = Math.floor(cur - 1e-9);
    const capacity = cur - dayFloor;
    const consume = Math.min(remaining, capacity);
    cur -= consume;
    remaining -= consume;
    if (remaining > 1e-9) cur = skipWeekendBackward(cur);
  }
  return cur;
}

/**
 * One task's own weekend eligibility: its own override if it has one ("never"/"always"),
 * otherwise the project's global `allowWeekendWork` setting ("auto", the default).
 */
export function taskAllowsWeekend(t: Task, allowWeekendWork: boolean): boolean {
  const mode = t.weekendWork ?? "auto";
  if (mode === "always") return true;
  if (mode === "never") return false;
  return allowWeekendWork;
}

/**
 * How many shifts a day's worth of `t`'s own duration actually spans: 2 only when the
 * project runs two shifts a day *and* this particular activity has a second team on it
 * (`doubleShift`); 1 otherwise — a single-team activity keeps its duration at ordinary
 * calendar-day pace even when the rest of the project runs two shifts.
 */
export function taskShiftsPerDay(t: Task, projectShiftsPerDay: 1 | 2): 1 | 2 {
  return projectShiftsPerDay === 2 && t.doubleShift ? 2 : 1;
}

/** The last working day before `day` — `day - 1`, pulled back over a weekend when work isn't allowed then. */
export function previousWorkingDay(day: number, allowWeekendWork: boolean): number {
  let d = Math.floor(day + 1e-9) - 1;
  if (!allowWeekendWork) {
    while ((((d % 7) + 7) % 7) >= 5) d--;
  }
  return d;
}

/**
 * The day a milestone should be shown on: the day its predecessors actually finished,
 * not the CPM boundary day sitting right after (the first day a successor's *own*
 * work could occupy — meaningless for a milestone, which has none). Falls back to the
 * plain ES when there's nothing to shift back from — no predecessors, or when a pin
 * or the schedule floor is what actually pushed the milestone out rather than a
 * predecessor's finish.
 */
export function milestoneDisplayDay(
  t: Task,
  ES: Record<string, number>,
  EF: Record<string, number>,
  allowWeekendWork: boolean
): number {
  const own = taskAllowsWeekend(t, allowWeekendWork);
  const es = ES[t.id] ?? 0;
  if (!t.preds.length) return es;
  const predMax = Math.max(0, ...t.preds.map((p) => EF[p] ?? 0));
  const predEs = own ? predMax : skipWeekendForward(predMax);
  if (Math.abs(predEs - es) > 1e-6) return es; // a pin or the floor day is what's binding, not the predecessors
  return previousWorkingDay(es, own);
}

export interface ScheduleOptions {
  /**
   * No *unfinished* work may be scheduled before this project day — in practice the
   * later of the project start and today, from `scheduleFloorDay`. This is what makes
   * the plan self-correcting: anything not marked done by the end of its planned day
   * is floored at today on the next recalculation and pushes its successors along.
   *
   * Omit it (the default) and nothing is floored — exactly the behaviour from before
   * the board had a calendar.
   */
  floorDay?: number;
  /**
   * Working "units" per calendar day the *project* supports — 2 when it runs two
   * shifts a day, 1 otherwise (the default). This is a ceiling, not a per-task fact:
   * `taskShiftsPerDay` combines it with each task's own `doubleShift` flag to decide
   * whether that particular task's duration actually gets halved.
   */
  shiftsPerDay?: 1 | 2;
}

/** The project days a task's work was actually completed on: deduped, sorted, sane. */
export function completedDays(t: Task): number[] {
  const raw = t.doneDays ?? [];
  const clean = raw.filter((d) => Number.isFinite(d) && d >= 0).map((d) => Math.floor(d));
  return [...new Set(clean)].sort((a, b) => a - b);
}

/** A task's own planned duration, in calendar days, at the given project shift ceiling —
 *  shared by the `effectiveActual*` derivation below and anything else that needs to
 *  know when a task's done days add up to the whole plan. */
function taskPlannedDays(t: Task, projectShiftsPerDay: 1 | 2 = 1): number {
  if (t.type === "milestone") return 0;
  return Math.max(0, Number(t.m) || 0) / taskShiftsPerDay(t, projectShiftsPerDay);
}

/**
 * The project day this task's work actually began, for a task/phase-execution report.
 * `actualStart` is an explicit override (set by hand, or on P6 import for activities
 * with no day-by-day board history) — absent that, it's the earliest day in `doneDays`,
 * or null if no work has been logged at all.
 */
export function effectiveActualStart(t: Task): number | null {
  if (t.actualStart != null && Number.isFinite(t.actualStart)) return t.actualStart;
  const done = completedDays(t);
  return done.length ? done[0] : null;
}

/**
 * The project day this task's work actually finished. Only meaningful once every
 * planned day is done — a part-done task has no finish date yet, however far along it
 * is — so this mirrors the `complete` check `toggleTaskDayDone`/`computeCPM` already use.
 */
export function effectiveActualFinish(t: Task, projectShiftsPerDay: 1 | 2 = 1): number | null {
  if (t.actualFinish != null && Number.isFinite(t.actualFinish)) return t.actualFinish;
  const done = completedDays(t);
  if (!done.length) return null;
  const complete = done.length >= taskPlannedDays(t, projectShiftsPerDay);
  return complete ? done[done.length - 1] : null;
}

/**
 * Forward/backward-pass Critical Path Method over the task list.
 * Ported 1:1 from the original hand-rolled implementation (crane_critical_path.html) —
 * same algorithm, just typed. Honors each task's `pinnedDay` ("start no earlier than")
 * as a floor on its earliest start, same as before. When `allowWeekendWork` is off
 * (the default), weekends are treated as non-working days — durations skip over them,
 * same as Excel's WORKDAY.
 *
 * Progress is folded in two ways. A task's duration is only the work *still to do*
 * (planned duration less the days marked done), and a task whose planned days are all
 * done is fixed to the days it actually ran rather than being scheduled at all — so
 * history stays put while the remaining plan moves around it. A part-done task is
 * therefore split: its completed days sit where they happened, its remaining days get
 * rescheduled from `floorDay` forward.
 */
export function computeCPM(
  tasks: Task[],
  durKey: DurationKey,
  allowWeekendWork: boolean,
  opts: ScheduleOptions = {}
): CPMOutcome {
  const idx: Record<string, Task> = {};
  tasks.forEach((t) => { idx[t.id] = t; });

  const ids = tasks.map((t) => t.id);
  const idSet = new Set(ids);

  const badPreds: string[] = [];
  const succ: Record<string, string[]> = {};
  const indeg: Record<string, number> = {};
  ids.forEach((i) => { succ[i] = []; indeg[i] = 0; });

  tasks.forEach((t) => {
    t.preds.forEach((p) => {
      if (!idSet.has(p)) { badPreds.push(`${t.id} → ${p}`); return; }
      if (p === t.id) { badPreds.push(`${t.id} → itself`); return; }
      succ[p].push(t.id);
      indeg[t.id]++;
    });
  });

  let queue = ids.filter((i) => indeg[i] === 0);
  const topo: string[] = [];
  const indeg2 = { ...indeg };
  while (queue.length) {
    const u = queue.shift()!;
    topo.push(u);
    succ[u].forEach((v) => {
      indeg2[v]--;
      if (indeg2[v] === 0) queue.push(v);
    });
  }

  if (badPreds.length) {
    return {
      ok: false,
      error:
        "Unknown predecessor reference: " +
        badPreds.slice(0, 5).join(", ") +
        (badPreds.length > 5 ? ` (+${badPreds.length - 5} more)` : "") +
        ". Fix the Predecessors column — unrecognized IDs are ignored in the meantime.",
    };
  }
  if (topo.length !== ids.length) {
    const stuck = ids.filter((i) => topo.indexOf(i) === -1);
    return {
      ok: false,
      error: `Circular dependency detected among: ${stuck.slice(0, 8).join(", ")}. The schedule can't be calculated until the cycle is broken.`,
    };
  }

  const projectShiftsPerDay = opts.shiftsPerDay ?? 1;
  const ES: Record<string, number> = {};
  const EF: Record<string, number> = {};
  const complete: Record<string, boolean> = {};
  const remaining: Record<string, number> = {};
  topo.forEach((u) => {
    const t = idx[u];
    const own = taskAllowsWeekend(t, allowWeekendWork);
    const dur = (Math.max(0, Number(t[durKey]) || 0)) / taskShiftsPerDay(t, projectShiftsPerDay);
    const done = completedDays(t);
    const rem = Math.max(0, dur - done.length);
    remaining[u] = rem;
    complete[u] = done.length > 0 && rem === 0;

    if (complete[u]) {
      // Finished work is a historical fact: it sits on the days it actually happened,
      // and neither the floor nor a late predecessor moves it. Successors still see a
      // real EF, so the rest of the network is driven by what happened, not the plan.
      ES[u] = done[0];
      EF[u] = dur === 0 ? done[0] : done[done.length - 1] + 1;
      return;
    }

    let es = 0;
    t.preds.forEach((p) => { es = Math.max(es, EF[p]); });
    const pin = t.pinnedDay;
    if (pin != null && !isNaN(pin)) es = Math.max(es, pin);
    if (opts.floorDay != null) es = Math.max(es, opts.floorDay);
    // Part-done work resumes *after* the last day already closed out, so ticking off
    // today's work doesn't leave the remainder scheduled onto the same day again.
    if (done.length) es = Math.max(es, done[done.length - 1] + 1);
    if (!own) es = skipWeekendForward(es);
    ES[u] = es;
    EF[u] = advanceByWorkingDays(es, rem, own);
  });

  let projectEnd = 0;
  ids.forEach((i) => { projectEnd = Math.max(projectEnd, EF[i]); });

  const LF: Record<string, number> = {};
  const LS: Record<string, number> = {};
  for (let k = topo.length - 1; k >= 0; k--) {
    const u2 = topo[k];
    // Completed work has no float to compute — it already happened. Reporting its
    // actual days as its latest days keeps successors' slack honest, instead of
    // letting a finished multi-day task look like it had days of spare time.
    if (complete[u2]) {
      LF[u2] = EF[u2];
      LS[u2] = ES[u2];
      continue;
    }
    const own2 = taskAllowsWeekend(idx[u2], allowWeekendWork);
    const s = succ[u2];
    let lf: number;
    if (!s.length) {
      lf = projectEnd;
    } else {
      let minLS = Infinity;
      s.forEach((v) => { minLS = Math.min(minLS, LS[v]); });
      lf = minLS;
    }
    if (!own2) lf = skipWeekendBackward(lf);
    LF[u2] = lf;
    LS[u2] = retreatByWorkingDays(lf, remaining[u2], own2);
  }

  const critical: Record<string, boolean> = {};
  ids.forEach((i) => { critical[i] = !complete[i] && Math.abs(LS[i] - ES[i]) < 1e-6; });

  return { ok: true, topo, succ, ES, EF, LS, LF, critical, complete, remaining, projectEnd };
}
